import { track } from '@bnnvara/analytics/src/track';
import { Close } from '@bnnvara/icons/src/components/UserInterfaceIcons/Close';
import BaseNotification from '@bnnvara/notifications';
import { differenceInSeconds, isSameDay } from 'date-fns';
import PropTypes from 'prop-types';
import React, { memo, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';

import * as S from './styled';

function isFirstSession(datetimeCookieAccepted, maxAge) {
  if (!datetimeCookieAccepted) {
    return false;
  }

  // we check how long ago the user accepted the cookies for the first time ( === started their first session)
  const secondsSinceCookieConsent = differenceInSeconds(
    new Date(),
    datetimeCookieAccepted
  );

  return secondsSinceCookieConsent < maxAge;
}

function alreadySeenToday(lastSeen) {
  return lastSeen && isSameDay(lastSeen, new Date());
}

const Notification = memo(
  ({
    visible,
    title,
    children,
    type,
    closeText,
    horizontalPosition,
    direction,
    delay,
    onClose,
    hideAfter,
    onlyShowOnceEvery24Hrs,
    showCloseButton,
  }) => {
    const [isNotificationDisabled, setIsNotificationDisabled] = useState(false);
    const [ready, setReady] = useState(false);
    const [cookies, setCookie] = useCookies(['cookie-name']);
    const lastSeen = cookies[type] ? cookies[type] : false;

    useEffect(() => {
      // if onlyShowOnceEvery24Hrs is true, do not show notification if the lastSeen date has the same date as today,
      // except for users that visit the site for the first time.
      if (
        onlyShowOnceEvery24Hrs &&
        alreadySeenToday(lastSeen) &&
        !isFirstSession(cookies.Cookie_Consent, 10)
      ) {
        setIsNotificationDisabled(true);
      } else {
        setCookie(type, new Date());
      }
    }, []);

    // Show the notification with a delay
    useEffect(() => {
      let timer;

      if (delay !== 0) {
        timer = setTimeout(() => {
          setReady(true);
        }, delay);
      } else {
        setReady(true);
      }

      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    }, [delay]);

    // Hide the notification with a delay
    useEffect(() => {
      let timer;

      if (hideAfter) {
        timer = setTimeout(() => {
          setIsNotificationDisabled(true);
        }, hideAfter + delay);
      }

      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    }, [hideAfter]);

    const disableNotification = () => {
      setIsNotificationDisabled(true);

      if (typeof onClose === 'function') {
        onClose();
        track(`Notification-${type}`, 'click', 'sluiten');
      }
    };

    if (!ready) {
      return null;
    }

    return (
      <BaseNotification
        visible={visible}
        onClose={disableNotification}
        disabled={isNotificationDisabled}
        horizontalPosition={horizontalPosition}
        direction={direction}
      >
        {title && <S.StyledTitle>{title}</S.StyledTitle>}
        {children}
        {closeText && (
          <S.NotificationCloseText onClick={disableNotification}>
            {closeText}
          </S.NotificationCloseText>
        )}
        {showCloseButton && (
          <S.CloseButton
            onClick={disableNotification}
            disabled={isNotificationDisabled}
          >
            <Close color="black" size={18} />
          </S.CloseButton>
        )}
      </BaseNotification>
    );
  }
);

Notification.defaultProps = {
  horizontalPosition: 'center',
  direction: 'down',
  delay: 0,
  onClose: null,
  children: null,
  closeText: null,
  hideAfter: null,
  onlyShowOnceEvery24Hrs: false,
  showCloseButton: true,
};

Notification.propTypes = {
  title: PropTypes.string,
  type: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired,
  children: PropTypes.node,
  delay: PropTypes.number,
  closeText: PropTypes.string,
  horizontalPosition: PropTypes.string,
  direction: PropTypes.string,
  onClose: PropTypes.func,
  hideAfter: PropTypes.number,
  onlyShowOnceEvery24Hrs: PropTypes.bool,
  showCloseButton: PropTypes.bool,
};

export default Notification;
